import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Coins, Check, Loader2, Zap, CreditCard, Sparkles, ShieldCheck, Lock, Gift, Ticket } from "lucide-react";
import { packTag, packPerks } from "@/i18n/catalog";
import { FaCcVisa, FaCcMastercard, FaCcAmex, FaApplePay, FaCcPaypal, FaCcStripe, FaPaypal } from "react-icons/fa";
import { useI18n } from "@/i18n";
import { detectCurrency, saveCurrency, fmtCur, curFlag, CUR_LABELS } from "@/lib/currency";

export const BuyCredits = () => {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [packages, setPackages] = useState([]);
  const [offer, setOffer] = useState({ eligible: false, discount_pct: 0, paypal_enabled: false });
  const [params] = useSearchParams();
  const [loadingId, setLoadingId] = useState(null);
  const [ppLoadingId, setPpLoadingId] = useState(null);
  const [polling, setPolling] = useState(false);
  const [cur, setCur] = useState("eur");
  const [promoCode, setPromoCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => { setCur(detectCurrency()); }, []);
  const changeCur = (c) => { setCur(c); saveCurrency(c); };

  useEffect(() => {
    const p = params.get("promo");
    if (p) setPromoCode(p.toUpperCase());
  }, [params]);

  const redeem = async () => {
    const code = promoCode.trim().toUpperCase();
    if (!code) { toast.error(t("credits.promoEnter") || "Entrez un code."); return; }
    setRedeeming(true);
    try {
      const { data } = await api.post("/promo/redeem", { code });
      await refresh();
      api.get("/payments/offer").then(({ data: o }) => setOffer(o)).catch(() => {});
      if (data.reward_type === "discount") {
        toast.success(`-${data.discount_pct}% appliqué à votre prochain achat 🎉${data.label ? ` — ${data.label}` : ""}`);
      } else {
        toast.success(`+${data.credits} crédits ajoutés 🎉${data.label ? ` — ${data.label}` : ""}`);
      }
      setPromoCode("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Code invalide");
    } finally {
      setRedeeming(false);
    }
  };

  useEffect(() => {
    api.get("/catalog").then(({ data }) => setPackages(data.packages));
    api.get("/payments/offer").then(({ data }) => setOffer(data)).catch(() => {});
  }, []);

  const pollStatus = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 6) { setPolling(false); toast.error(t("credits.timeout")); return; }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        await refresh();
        setPolling(false);
        toast.success(t("credits.paySuccess", { n: data.credits }));
        navigate("/client/credits", { replace: true });
        return;
      }
      if (data.status === "expired") { setPolling(false); toast.error(t("credits.sessionExpired")); return; }
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
    } catch (e) {
      setPolling(false);
      toast.error(t("credits.verifyError"));
    }
  }, [refresh, navigate, t]);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (sessionId) {
      setPolling(true);
      pollStatus(sessionId);
    }
  }, [params, pollStatus]);

  // PayPal return: capture the approved order then credit.
  useEffect(() => {
    if (params.get("paypal") === "cancel") {
      toast.info(t("credits.ppCancel"));
      navigate("/client/credits", { replace: true });
      return;
    }
    const ppOrder = params.get("paypal") === "return" ? params.get("token") : null;
    if (!ppOrder) return;
    setPolling(true);
    (async () => {
      try {
        const { data } = await api.post("/payments/paypal/capture-order", { order_id: ppOrder });
        if (data.payment_status === "paid") {
          await refresh();
          toast.success(t("credits.ppSuccess", { n: data.credits }));
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || t("credits.ppError"));
      } finally {
        setPolling(false);
        navigate("/client/credits", { replace: true });
      }
    })();
  }, [params, refresh, navigate, t]);

  const buy = async (pkg) => {
    setLoadingId(pkg.id);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: pkg.id,
        origin_url: window.location.origin,
        use_welcome_discount: offer.eligible,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("credits.payError"));
      setLoadingId(null);
    }
  };

  const buyPaypal = async (pkg) => {
    setPpLoadingId(pkg.id);
    try {
      const { data } = await api.post("/payments/paypal/create-order", {
        package_id: pkg.id,
        origin_url: window.location.origin,
        use_welcome_discount: offer.eligible,
      });
      if (data.approve_url) {
        window.location.href = data.approve_url;
      } else {
        throw new Error("no approve url");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PayPal");
      setPpLoadingId(null);
    }
  };

  const promoPct = offer.promo_discount?.pct || 0;
  const welcomePct = offer.eligible ? (offer.discount_pct || 0) : 0;
  const bestPct = Math.max(promoPct, welcomePct);
  const bestActive = bestPct > 0;

  const discounted = (price) =>
    bestActive ? (price * (1 - bestPct / 100)).toFixed(2) : null;

  const need = parseInt(params.get("need") || "0", 10);
  const unlock = params.get("unlock") || "";
  const recommended = need > 0
    ? [...packages].sort((a, b) => a.credits - b.credits).find((p) => p.credits >= need)
    : null;

  return (
    <AppShell mode="client">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-xs text-aurora-cyan tracking-[0.3em] uppercase mb-2">{t("credits.kicker")}</div>
          <h1 className="font-display text-4xl font-bold uppercase tracking-tight">{t("credits.title")}</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="glass rounded-md px-3 py-2 flex items-center gap-2" data-testid="currency-selector">
            <img src={curFlag(cur)} alt="" className="w-5 h-auto rounded-sm" />
            <select
              value={cur}
              onChange={(e) => changeCur(e.target.value)}
              data-testid="currency-select"
              aria-label={t("credits.curLabel")}
              className="bg-transparent font-mono text-xs uppercase tracking-widest text-zinc-300 focus:outline-none cursor-pointer"
            >
              {Object.keys(CUR_LABELS).map((c) => (
                <option key={c} value={c}>{CUR_LABELS[c]}</option>
              ))}
            </select>
          </div>
          <div className="glass rounded-md px-5 py-3 flex items-center gap-3">
            <Coins className="w-6 h-6 text-aurora-cyan" />
            <div>
              <div className="font-display text-2xl font-bold text-aurora-cyan leading-none">{user?.credits ?? 0}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{t("credits.balance")}</div>
            </div>
          </div>
        </div>
      </div>

      {cur !== "eur" && (
        <div className="mb-6 font-mono text-[11px] text-zinc-500 flex items-center gap-2" data-testid="currency-note">
          <img src={curFlag(cur)} alt="" className="w-4 h-auto rounded-[2px]" />
          {t("credits.curNote")}
        </div>
      )}

      {need > 0 && (
        <div
          data-testid="unlock-banner"
          className="relative overflow-hidden rounded-md p-5 mb-6 border border-signal-success/40 bg-signal-success/5"
        >
          <div className="m-stripe-bar absolute top-0 left-0" />
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 grid place-items-center rounded-sm bg-signal-success/15 border border-signal-success/40 shrink-0">
              <Lock className="w-6 h-6 text-signal-success" />
            </div>
            <div className="min-w-0">
              <div className="font-display text-lg font-bold uppercase tracking-tight text-white">
                {t("credits.unlock")} {unlock ? <span className="text-signal-success">{unlock}</span> : null}
              </div>
              <p className="font-mono text-xs text-zinc-400 mt-0.5">
                {t("credits.unlockMissing", { n: need })}
                {recommended && <> {t("credits.recommended")} <span className="text-white font-bold">{recommended.label}</span>.</>}
              </p>
            </div>
          </div>
        </div>
      )}

      {offer.promo?.active && (offer.promo.text || offer.promo.discount_pct > 0) && (
        <div
          data-testid="promo-banner"
          className="relative overflow-hidden rounded-md p-5 mb-6 flex items-center gap-4 border border-aurora-purple/40 bg-aurora-purple/10"
        >
          <div className="m-stripe-bar absolute top-0 left-0" />
          <div className="w-11 h-11 grid place-items-center rounded-sm bg-aurora-purple/20 border border-aurora-purple/40 shrink-0">
            <Gift className="w-6 h-6 text-aurora-purple" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display text-lg font-bold uppercase tracking-tight text-white flex items-center gap-2 flex-wrap">
              {offer.promo.text || t("credits.promoDefault", { pct: offer.promo.discount_pct })}
              {offer.promo.discount_pct > 0 && (
                <span className="font-mono text-[10px] uppercase tracking-widest font-bold text-aurora-purple bg-aurora-purple/15 px-2 py-0.5 rounded-sm">-{offer.promo.discount_pct}%</span>
              )}
            </div>
            {offer.promo.ends_at && (
              <p className="font-mono text-[11px] text-zinc-400 mt-1">
                {t("credits.promoEnds")} {new Date(offer.promo.ends_at).toLocaleString()}
              </p>
            )}
          </div>
        </div>
      )}

      {offer.promo_discount?.pct > 0 && (
        <div
          data-testid="promo-discount-banner"
          className="relative overflow-hidden rounded-md p-5 mb-6 flex items-center gap-4 border border-aurora-purple/40 bg-aurora-purple/10"
        >
          <div className="m-stripe-bar absolute top-0 left-0" />
          <div className="w-11 h-11 grid place-items-center rounded-sm bg-aurora-purple/20 border border-aurora-purple/40 shrink-0">
            <Sparkles className="w-6 h-6 text-aurora-purple" />
          </div>
          <div className="min-w-0">
            <div className="font-display text-lg font-bold uppercase tracking-tight text-white flex items-center gap-2 flex-wrap">
              Code promo actif — -{offer.promo_discount.pct}%
              <span className="font-mono text-[10px] uppercase tracking-widest font-bold text-aurora-purple bg-aurora-purple/15 px-2 py-0.5 rounded-sm">{offer.promo_discount.code}</span>
            </div>
            <p className="font-mono text-xs text-zinc-400 mt-0.5">Réduction appliquée automatiquement sur votre prochain achat de crédits.</p>
          </div>
        </div>
      )}

      {offer.eligible && (
        <div
          data-testid="welcome-offer-banner"
          className="relative overflow-hidden rounded-md p-5 mb-6 flex items-center gap-4 bg-aurora-cyan"
        >
          <div className="m-stripe-bar absolute top-0 left-0" />
          <div className="w-11 h-11 grid place-items-center rounded-sm bg-white/15 shrink-0">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div className="min-w-0">
            <div className="font-display text-lg font-bold uppercase tracking-tight text-white">
              {t("credits.welcome", { pct: offer.discount_pct })}
            </div>
            <p className="font-mono text-xs text-white/85 mt-0.5">
              {t("credits.welcomeDesc")}
            </p>
          </div>
        </div>
      )}

      {polling && (
        <div className="glass rounded-md p-4 mb-6 flex items-center gap-3 border-l-2 border-signal-processing">
          <Loader2 className="w-5 h-5 animate-spin text-signal-processing" />
          <span className="font-mono text-xs text-zinc-300">{t("credits.verifying")}</span>
        </div>
      )}

      <div className="glass rounded-md p-5 mb-6 border-l-2 border-aurora-purple/50" data-testid="promo-redeem-card">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 grid place-items-center rounded-sm bg-aurora-purple/15 border border-aurora-purple/40 shrink-0">
              <Ticket className="w-6 h-6 text-aurora-purple" />
            </div>
            <div className="min-w-0">
              <div className="font-display text-base font-bold uppercase tracking-tight">Code promo / cadeau</div>
              <p className="font-mono text-[11px] text-zinc-500 mt-0.5">Reçu sur un forum ? Saisissez-le pour créditer votre compte.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:ml-auto w-full sm:w-auto">
            <input
              data-testid="promo-code-input"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && redeem()}
              placeholder="IMP-XXXXXX"
              className="flex-1 sm:w-44 bg-black/50 border border-white/10 text-white px-3 py-2.5 rounded-sm text-sm font-mono uppercase tracking-wider focus:border-aurora-purple focus:outline-none"
            />
            <button
              data-testid="promo-redeem-btn"
              onClick={redeem}
              disabled={redeeming}
              className="flex items-center justify-center gap-2 px-5 py-2.5 bg-aurora-purple text-white font-display font-bold uppercase tracking-wider rounded-sm hover:bg-violet-500 transition-colors disabled:opacity-60 whitespace-nowrap"
            >
              {redeeming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}
              Valider
            </button>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {packages.map((p) => (
          <div
            key={p.id}
            data-testid={`package-${p.id}`}
            className={`relative glass rounded-md p-6 flex flex-col transition-all hover:shadow-glow ${recommended?.id === p.id ? "ring-2 ring-signal-success shadow-glow border-signal-success/50" : p.popular ? "border-aurora-cyan/50 shadow-glow" : "hover:border-aurora-cyan/30"}`}
          >
            {recommended?.id === p.id ? (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-signal-success text-black font-mono text-[9px] uppercase tracking-widest font-bold rounded-sm flex items-center gap-1 whitespace-nowrap">
                <Lock className="w-3 h-3" /> {t("credits.unlocksFile")}
              </div>
            ) : p.popular && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-aurora-cyan text-black font-mono text-[9px] uppercase tracking-widest font-bold rounded-sm flex items-center gap-1">
                <Zap className="w-3 h-3" /> {packTag(p, t)}
              </div>
            )}
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{p.label}</div>
            <div className="mt-3 flex items-baseline gap-2 flex-wrap">
              {bestActive ? (
                <>
                  <span className="font-display text-4xl font-bold text-white" data-testid={`price-${p.id}`}>{discounted(p.price)}€</span>
                  <span className="font-mono text-base text-zinc-500 line-through">{p.price}€</span>
                  <span className="font-mono text-[9px] uppercase tracking-widest font-bold text-signal-success bg-signal-success/10 px-1.5 py-0.5 rounded-sm">-{bestPct}%</span>
                </>
              ) : (
                <span className="font-display text-4xl font-bold text-white" data-testid={`price-${p.id}`}>{p.price}€</span>
              )}
            </div>
            {(() => {
              const ht = bestActive ? p.price * (1 - bestPct / 100) : p.price;
              const rate = offer.vat_rate ?? 21;
              const ttc = ht * (1 + rate / 100);
              return (
                <div className="mt-1.5 font-mono text-[10px] text-zinc-500" data-testid={`vat-${p.id}`}>
                  {offer.vat_exempt
                    ? <>HT {ht.toFixed(2)}€ · <span className="text-signal-success">{t("credits.vatExempt")}</span></>
                    : <>HT {ht.toFixed(2)}€ + TVA {rate}% = <span className="text-white font-bold">{ttc.toFixed(2)}€ TTC</span></>}
                  {cur !== "eur" && (
                    <span className="block text-aurora-cyan/80 mt-0.5" data-testid={`indicative-${p.id}`}>
                      ≈ {fmtCur(offer.vat_exempt ? ht : ttc, cur)} {CUR_LABELS[cur]}
                    </span>
                  )}
                </div>
              );
            })()}
            <div className="mt-1 flex items-center gap-1.5 text-aurora-cyan">
              <Coins className="w-4 h-4" />
              <span className="font-mono text-lg font-bold">{p.credits}</span>
              <span className="font-mono text-xs text-zinc-500">{t("credits.creditsWord")}</span>
            </div>
            <ul className="mt-5 space-y-2 flex-1">
              {packPerks(p, t).map((perk) => (
                <li key={perk} className="flex items-center gap-2 text-xs text-zinc-300">
                  <Check className="w-3.5 h-3.5 text-signal-success shrink-0" /> {perk}
                </li>
              ))}
            </ul>
            <button
              data-testid={`buy-${p.id}-btn`}
              onClick={() => buy(p)}
              disabled={loadingId !== null || ppLoadingId !== null}
              className={`mt-6 w-full flex items-center justify-center gap-2 py-3 font-display font-bold uppercase tracking-wider rounded-sm transition-colors disabled:opacity-60 ${p.popular ? "bg-aurora-cyan text-black hover:bg-cyan-300 shadow-glow" : "border border-white/20 text-white hover:bg-white/5"}`}
            >
              {loadingId === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
              {offer.paypal_enabled ? t("credits.card") : t("credits.buy")}
            </button>
            {offer.paypal_enabled && (
              <button
                data-testid={`buy-${p.id}-paypal-btn`}
                onClick={() => buyPaypal(p)}
                disabled={loadingId !== null || ppLoadingId !== null}
                className="mt-2.5 w-full flex items-center justify-center gap-2 py-3 font-display font-bold uppercase tracking-wider rounded-sm transition-colors disabled:opacity-60 bg-[#ffc439] text-[#003087] hover:bg-[#f5b800]"
              >
                {ppLoadingId === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <FaPaypal className="w-4 h-4" />}
                PayPal
              </button>
            )}
          </div>
        ))}
      </div>

      <div className="mt-10 glass rounded-md p-6" data-testid="trust-badges">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-5">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 grid place-items-center rounded-sm bg-signal-success/10 border border-signal-success/30 shrink-0">
              <ShieldCheck className="w-6 h-6 text-signal-success" />
            </div>
            <div>
              <div className="font-display text-sm font-bold uppercase tracking-tight flex items-center gap-2">
                <Lock className="w-3.5 h-3.5 text-signal-success" /> {t("credits.secureTitle")}
              </div>
              <p className="font-mono text-[10px] text-zinc-500 mt-0.5">{t("credits.secureDesc")}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 text-zinc-400">
            <FaCcVisa className="w-9 h-9 hover:text-white transition-colors" title="Visa" />
            <FaCcMastercard className="w-9 h-9 hover:text-white transition-colors" title="Mastercard" />
            <FaCcAmex className="w-9 h-9 hover:text-white transition-colors" title="American Express" />
            <FaApplePay className="w-10 h-10 hover:text-white transition-colors" title="Apple Pay" />
            <FaCcPaypal className="w-9 h-9 hover:text-white transition-colors" title="PayPal" />
            <FaCcStripe className="w-9 h-9 hover:text-white transition-colors" title="Stripe" />
          </div>
        </div>
      </div>

      <p className="mt-6 text-center font-mono text-[10px] text-zinc-600 tracking-wider uppercase">
        {t("credits.footerSecure")}{offer.paypal_enabled ? t("credits.ppAvail") : t("credits.ppSoon")}
      </p>
    </AppShell>
  );
};
